import { useStaticQuery, graphql } from "gatsby"
import { telephoneStrip, convertTime12to24 } from "../utils/utils"

const useSchemas = schemaData => {
  const { site, siteVariables, locations } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          description
          siteUrl
          fullPracticeName
        }
      }
      siteVariables: allDataJson(
        filter: { optionsItem: { eq: "siteVariables" } }
      ) {
        nodes {
          datePublished
          socialImage
          logo
          clientType
          productontology {
            name
            link
            relatedLinks
          }
        }
      }
      locations: allLocationsJson {
        nodes {
          locationData {
            locationHours {
              startTime
              weekDay
              endTime
            }
            coords {
              lat
              lng
            }
            areaServed
            stateAbbr
            stateName
            streetName
            streetNumber
            cityName
            cityZip
            phone
            googlePlaceLink
            socialAccounts {
              link
            }
          }
          pageData {
            title
          }
        }
      }
    }
  `)
  const locationsFormatted = locations.nodes.map(l => ({
    ...l.locationData,
    slug: l.pageData.title
  }))
  const data = {
    ...schemaData,
    ...site.siteMetadata,
    ...siteVariables.nodes[0],
    locations: locationsFormatted
  }

  const clientTypes = {
    oms: "http://www.productontology.org/id/Oral_and_maxillofacial_surgery",
    dentist: "http://www.productontology.org/id/Dentist",
    ortho: "http://www.productontology.org/id/Orthodontics",
    endo: "http://www.productontology.org/id/Endodontics"
  }

  // @type: "WebPage"
  const createWebpageSchema = data => ({
    "@type": "WebPage",
    "@id": `${data.siteUrl + data.path}#webpage`,
    url: data.siteUrl + data.path,
    inLanguage: data.language === "en" ? "en-US" : null,
    name: data.fullPracticeName,
    datePublished: data.datePublished,
    dateModified: data.dateModified,
    description: data.metaDescription || data.description
  })

  // @type: "WebSite"
  const createWebsiteSchema = data => ({
    "@type": "WebSite",
    "@id": `${data.siteUrl}/#website`,
    url: `${data.siteUrl}/`,
    thumbnailUrl: data.socialImage,
    name: data.fullPracticeName,
    potentialAction: {
      "@type": "SearchAction",
      target: `${data.siteUrl}/?s={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  })

  // @type: Organization
  const createOrganizationSchema = data => {
    let sameAsArr = []
    const googleLinks = data.locations.map(link => link.googlePlaceLink)
    const socials = data.locations[0].socialAccounts.map(link => link.link)

    sameAsArr = [...googleLinks, ...socials]

    return {
      "@type": "Organization",
      "@id": `${data.siteUrl}/`,
      name: data.fullPracticeName,
      url: `${data.siteUrl}/`,
      logo: data.logo,
      sameAs: sameAsArr
    }
  }

  // @type: localBusiness
  const createBusinessSchema = (data, location, isSingle) => {
    // weekday info map
    const weekdays = {
      0: { abbr: "Su", order: 7 },
      1: { abbr: "Mo", order: 1 },
      2: { abbr: "Tu", order: 2 },
      3: { abbr: "We", order: 3 },
      4: { abbr: "Th", order: 4 },
      5: { abbr: "Fr", order: 5 },
      6: { abbr: "Sa", order: 6 }
    }
    const h = location.locationHours

    // loop 7 times AKA 1 week
    for (let i = 0; i <= 6; i++) {
      // check if location has hours set for a day in 'weekdays' map
      const find = h.find(day => day.weekDay == i)

      if (!find) {
        // if no hours, set them the hours to 00000
        weekdays[i].abbr += " 00:00-00:00"
      } else {
        // else format hours to 24hour format
        const hours = `${convertTime12to24(find.startTime)}-${convertTime12to24(
          find.endTime
        )}`
        weekdays[find.weekDay].abbr += ` ${hours}`
      }
    }

    // sort hours by weekday. start on monday, end on sunday
    const sortedHours = Object.values(weekdays)
      .sort((a, b) => a.order - b.order)
      .map(days => days.abbr)

    // extra schema properties if a single location page
    let singleLocationValues = {}
    if (isSingle) {
      singleLocationValues = {
        geo: {
          "@type": "GeoCoordinates",
          latitude: location.coords.lat,
          longitude: location.coords.lng
        },
        areaServed: [location.areaServed],
        priceRange: "unset"
      }
    }

    return {
      "@type": "LocalBusiness",
      additionalType: [clientTypes[data.clientType]],
      parentOrganization: {
        name: data.fullPracticeName,
        "@id": `${data.siteUrl}/#organization`
      },
      image: data.socialImage,
      priceRange: "unset",
      name: `${data.fullPracticeName} - ${location.cityName}`,
      address: {
        "@type": "PostalAddress",
        streetAddress: `${location.streetNumber} ${location.streetName}`,
        addressLocality: location.cityName,
        addressRegion: location.stateAbbr,
        postalCode: location.cityZip,
        telephone: `+1${telephoneStrip(location.phone)}`
      },
      openingHours: sortedHours,
      hasmap: location.googlePlaceLink,
      ...singleLocationValues
    }
  }

  // @type: MedicalProcedure
  const createMedicalProcedureSchema = (data, isSingle) => {
    const medical = {}

    // filter all productontology objects to use the ones single procedure page is asking for
    const findMatches = data.productontology.filter(item =>
      data.productontologyRelations.includes(item.name)
    )

    if (findMatches && findMatches.length > 0) {
      const types = []
      const related = []

      // loop through matches and push links to arrays
      findMatches.forEach(item => {
        types.push(item.link)
        if (item.relatedLinks) {
          item.relatedLinks.forEach(link => {
            related.push(link)
          })
        }
      })

      // create additionalType key/value
      medical.additionalType = types.length === 1 ? types[0] : types

      // create sameAs key/value if there are related links
      if (related.length > 0) {
        medical.sameAs = related
      }
    }

    return {
      "@context": "https://schema.org/",
      "@type": "MedicalProcedure",
      name: data.fullPracticeName,
      url: `${data.siteUrl}${data.path}`,
      image: data.socialImage,
      description: data.pageContent,
      ...medical
    }
  }

  // loop through schema presets and push final schemas into array
  const schemas = []

  if (data.presets && data.presets.length > 0) {
    data.presets.forEach(type => {
      switch (type) {
        case "webpage":
          schemas.push(createWebpageSchema(data))
          break

        case "website":
          schemas.push(createWebsiteSchema(data))
          break

        case "organization":
          schemas.push(createOrganizationSchema(data))
          break

        case "allLocations":
          data.locations.forEach(location => {
            schemas.push(createBusinessSchema(data, location))
          })
          break

        case "singleLocation": {
          const find = data.locations.find(
            location => `/${location.slug}` === data.path
          )
          if (find) {
            schemas.push(createBusinessSchema(data, find, true))
          }
          break
        }

        case "medicalQuery":
          schemas.push(createMedicalProcedureSchema(data))
          break

        case "singleMedical":
          schemas.push(createMedicalProcedureSchema(data, true))
          break

        case "custom":
          schemas.push(data.customSchema)
          break
        default:
          return []
      }
    })
  }

  return {
    schemas
  }
}

export default useSchemas
